export type SupportedLanguages = 'de' | 'it' | 'en' | 'fr' | 'pl';

export const SUPPORTED_LANGUAGES: {
  [key in SupportedLanguages]: boolean;
} = {
  de: true,
  it: true,
  en: true,
  fr: true,
  pl: true,
};

export type SupportedCurrency = 'EUR';

export type PhoneNumber = {
  dialCode: string;
  number?: string;
};

export interface MonetaryValue {
  amount: number | null;
  currency: SupportedCurrency;
}

export const ALL_LOCALES: {
  keyLocale: string;
  value: SupportedLanguages;
  displayName: string;
}[] = [
  {
    keyLocale: 'English',
    displayName: 'English',
    value: 'en',
  },
  {
    keyLocale: 'German',
    displayName: 'Deutsch',
    value: 'de',
  },
  {
    keyLocale: 'Italian',
    displayName: 'Italiano',
    value: 'it',
  },
  {
    keyLocale: 'French',
    displayName: 'Français',
    value: 'fr',
  },
  {
    keyLocale: 'Polish',
    displayName: 'Polski',
    value: 'pl',
  },
];

export const getAvailableLocales = (activeLanguage: SupportedLanguages[]) => {
  return ALL_LOCALES.filter((locale) => activeLanguage.includes(locale.value));
};

export const SUPPORTED_LANGUAGE_CODES = Object.keys(SUPPORTED_LANGUAGES) as SupportedLanguages[];

// if no languages are defined, the system available language should be limited to DE/IT/EN only to stay backward compatible.
export const DEFAULT_HOTEL_AVAILABLE_LANGUAGES: SupportedLanguages[] = ['en', 'de', 'it'];

export type MultiLangContent<ContentType = string> = {
  type: 'multi_lang';
  values: {
    [key in SupportedLanguages]?: ContentType;
  };
};

export type GenericMultiLangContentObject = {
  [key: string]: unknown | MultiLangContent | GenericMultiLangContentObject;
};

export type TranslationsStatus = {
  [key in SupportedLanguages]?: {
    missingKeys?: string[];
    allKeysSet?: boolean;
  };
};

function isMultiLangContentObject(content: unknown): content is MultiLangContent {
  return (
    typeof content === 'object' &&
    content !== null &&
    'type' in content &&
    (content as { type: unknown }).type === 'multi_lang'
  );
}

const langCodes: Record<SupportedLanguages, boolean> = {
  en: true,
  de: true,
  it: true,
  fr: true,
  pl: true,
};

export const ALL_LANG_CODES = Object.keys(langCodes) as SupportedLanguages[];

export function findMissingTranslations(
  content: GenericMultiLangContentObject,
  supportedLocales: SupportedLanguages[]
): TranslationsStatus {
  const missingTranslations: TranslationsStatus = {};

  const findMissingTranslationsRecursively = (
    content: GenericMultiLangContentObject,
    path: string[] = []
  ) => {
    for (const [key, value] of Object.entries(content)) {
      if (typeof value === 'object' && value !== null) {
        if (isMultiLangContentObject(value) && key !== 'image') {
          const isAnyLanguageDefined = supportedLocales.some(
            (language) => !!value.values[language]
          );

          const missingLanguages = !isAnyLanguageDefined
            ? []
            : supportedLocales.filter((language) => !value.values[language]);
          if (missingLanguages.length > 0) {
            missingLanguages.forEach((language) => {
              if (!missingTranslations[language]) {
                missingTranslations[language] = {
                  missingKeys: [],
                };
              }
              const keysArr = missingTranslations[language];
              (keysArr as NonNullable<typeof keysArr>).missingKeys?.push([...path, key].join('.'));
            });
          }

          if (!isAnyLanguageDefined) {
            for (const language of supportedLocales) {
              if (!missingTranslations[language]) {
                missingTranslations[language] = {
                  missingKeys: [],
                  allKeysSet: false,
                };
              }
              const keysArr = missingTranslations[language];
              (keysArr as NonNullable<typeof keysArr>).allKeysSet = false;
            }
          }
        } else {
          findMissingTranslationsRecursively(value as GenericMultiLangContentObject, [
            ...path,
            key,
          ]);
        }
      }
    }
  };

  findMissingTranslationsRecursively(content);

  for (const lang of supportedLocales) {
    if (
      (!missingTranslations[lang] || missingTranslations[lang]?.missingKeys?.length === 0) &&
      missingTranslations[lang]?.allKeysSet !== false
    ) {
      missingTranslations[lang] = {
        allKeysSet: true,
      };
    }
  }

  return missingTranslations;
}

type FieldMissing = {
  fieldName: string;
  language: SupportedLanguages;
  status: TranslationsStatus;
};

export function isFieldMissing({ fieldName, language, status }: FieldMissing) {
  return status[language]?.missingKeys?.includes(fieldName) || false;
}

function getValueFromKey<TData = string>(
  key: string,
  values: GenericMultiLangContentObject
): TData | undefined {
  const keys = key.split('.');
  let value: unknown = values;
  for (const k of keys) {
    if (value === undefined || value === null || typeof value !== 'object') {
      return value as TData;
    }
    const nextVal = (value as Record<string, GenericMultiLangContentObject>)?.[k];

    value = nextVal;
  }
  return value as TData;
}

type AvailableTranslation = {
  language: SupportedLanguages;
  value: string;
};

function getAvailableTranslation(
  content: MultiLangContent,
  preferredLang: SupportedLanguages
): AvailableTranslation | undefined {
  const preferredLangValue = content.values[preferredLang];
  if (preferredLangValue) {
    return {
      language: preferredLang,
      value: preferredLangValue,
    };
  }
  const found = Object.entries(content.values).find(([, value]) => !!value);

  if (!found) return undefined;

  const [language, value] = found;

  return {
    language: language as SupportedLanguages,
    value,
  };
}

type CreateTranslationTargetsParams = {
  values: GenericMultiLangContentObject;
  preferedSourceLanguage: SupportedLanguages;
  supportedLocales: SupportedLanguages[];
};

type MissingTranslation = {
  key: string;
  sourceTranslation: AvailableTranslation;
  translateTo: SupportedLanguages[];
};

// now transform into easily translatable format
type TranslateTarget = {
  obj: Record<string, string>;
  sourceLang: SupportedLanguages;
  targetLang: SupportedLanguages;
};

export function createTranslationTargets({
  preferedSourceLanguage,
  values,
  supportedLocales,
}: CreateTranslationTargetsParams) {
  const translationsStatus = findMissingTranslations(values, supportedLocales);

  const missingLanguages = Object.entries(translationsStatus).filter(
    ([, obj]) => obj.missingKeys && obj.missingKeys.length > 0
  );

  const toTranslate: MissingTranslation[] = [];

  for (const [missingLangKey, missingLangObj] of missingLanguages) {
    const targetLang = missingLangKey as SupportedLanguages;
    if (!missingLangObj.missingKeys) continue;
    for (const missingKey of missingLangObj.missingKeys) {
      const foundKey = toTranslate.find((obj) => obj.key === missingKey);

      if (foundKey) {
        foundKey.translateTo.push(targetLang);
        continue;
      }

      const value = getValueFromKey<MultiLangContent>(missingKey, values);
      if (!value) continue;
      const availableLanguage = getAvailableTranslation(value, preferedSourceLanguage);
      if (!availableLanguage) continue;

      toTranslate.push({
        key: missingKey,
        sourceTranslation: availableLanguage,
        translateTo: [targetLang],
      });
    }
  }

  const translateTargets = toTranslate.reduce((targets, toTranslate) => {
    let newTargets = [...targets];

    const sourceLang = toTranslate.sourceTranslation.language;
    const targetLangs = toTranslate.translateTo;

    for (const targetLang of targetLangs) {
      let didFindTarget = false;

      newTargets = newTargets.map((target) => {
        if (target.sourceLang === sourceLang && target.targetLang === targetLang) {
          didFindTarget = true;
          return {
            ...target,
            obj: {
              ...target.obj,
              [toTranslate.key]: toTranslate.sourceTranslation.value,
            },
          };
        }
        return target;
      });

      if (!didFindTarget) {
        newTargets = [
          ...newTargets,
          {
            obj: {
              [toTranslate.key]: toTranslate.sourceTranslation.value,
            },
            sourceLang,
            targetLang,
          },
        ];
      }
    }

    return newTargets;
  }, [] as TranslateTarget[]);

  return translateTargets;
}

export function applyTranslation<
  TTranslationData extends GenericMultiLangContentObject = GenericMultiLangContentObject
>(
  translated: Record<string, string>,
  values: TTranslationData,
  targetLang: SupportedLanguages
): TTranslationData {
  const newValues = { ...values };

  for (const [key, value] of Object.entries(translated)) {
    const foundKey = getValueFromKey<MultiLangContent>(key, newValues);
    if (!foundKey) continue;
    foundKey.values[targetLang] = value;
  }

  return newValues;
}

export function getUserTimezoneId() {
  const tzId = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return tzId;
}

export type MultilangFormEditData<TData extends GenericMultiLangContentObject> = {
  data: TData;
  activeLanguage: SupportedLanguages;
};

export const defaultMultiLang: MultiLangContent = {
  type: 'multi_lang',
  values: {
    de: '',
    en: '',
    it: '',
    fr: '',
    pl: '',
  },
};

export function didMultiLangContentDiverge(
  obj1: GenericMultiLangContentObject,
  obj2: GenericMultiLangContentObject
) {
  const keys = new Set([...Object.keys(obj1), ...Object.keys(obj2)]);

  for (const key of keys) {
    const value1 = obj1[key];
    const value2 = obj2[key];

    if (isMultiLangContentObject(value1) && isMultiLangContentObject(value2)) {
      for (const lang of SUPPORTED_LANGUAGE_CODES) {
        if (value1.values[lang] !== value2.values[lang]) {
          return true;
        }
      }
    } else if (value1 !== value2) {
      return true;
    }
  }

  return false;
}
/**
 * Type predicate to narrow down the type of a language ISO string
 */

export const getAvailableLanguageValue = (
  content: MultiLangContent | undefined,
  defaultLanguage: SupportedLanguages
) => {
  if (!content) return '';
  if (content.values[defaultLanguage]) return content.values[defaultLanguage];
  const nextAvailableLanguage = SUPPORTED_LANGUAGE_CODES.find((language) =>
    Boolean(content.values[language])
  );
  return nextAvailableLanguage ? content.values[nextAvailableLanguage] : '';
};

export function isSupportedLanguage(langStr: string): langStr is SupportedLanguages {
  return SUPPORTED_LANGUAGE_CODES.includes(langStr as SupportedLanguages);
}

export const formatPrice = (lang: SupportedLanguages, price?: MonetaryValue) => {
  return price && price.amount
    ? price.amount.toLocaleString(lang, {
        style: 'currency',
        currency: 'EUR',
      })
    : '';
};

export function isMultiLangContent(value: any): value is MultiLangContent {
  return typeof value === 'object' && value.type === 'multi_lang';
}
